/****************************************************
 * Module Name:  stockTallyMain.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import * as React from "react";
import SwipeableViews from "react-swipeable-views";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import { useMediaQuery } from "@mui/material";
import Constants from "../../utility/constants";
import NoPermission from "../common/noPermission";
import TallyStock from "./tallyStock";
import TallyProformaSend from "./tallyProformaSend";
import TallyProformaReceive from "./tallyProformaReceive";
import TallyLocationTransferSend from "./tallyLocationTransferSend";
import TallyLocationTransferReceive from "./tallyLocationTransferReceive";
import TallyManufacturingSend from "./tallyManufacturingSend";
import TallyManufacturingReceive from "./tallyManufacturingReceive";
import SendIcon from '@mui/icons-material/Send';
import SendAndArchiveIcon from '@mui/icons-material/SendAndArchive';
import NearMeIcon from '@mui/icons-material/NearMe';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DiamondIcon from '@mui/icons-material/Diamond';
import GradingIcon from '@mui/icons-material/Grading';
import { FaBoxOpen } from "react-icons/fa";
import TallyBox from "./tallyBox";
import TallyGrading from "./tallyGrading";
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import TallyOffline from "./tallyOffline";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 1 }}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </Box>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        "aria-controls": `full-width-tabpanel-${index}`,
        value: index,
    };
}

export default function StockTallyMain() {
    const theme = useTheme();
    const [value, setValue] = React.useState(
        Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.OPERATION_STOCKTALLY
        ) !== undefined
            ? 0
            : 10
    );

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const matches = useMediaQuery("(min-width:1500px)");

    const handleChangeIndex = (index) => {
        setValue(index);
    };

    return (
        value === 10 ? <NoPermission /> :
            <Box sx={{ bgcolor: "background.paper", width: "100%", padding: "10px" }}>
                <AppBar position="static" sx={{ bgcolor: "#03a9f4", height: 70 }}>
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        indicatorColor="secondary"
                        textColor="inherit"
                        variant={matches ? "fullWidth" : "scrollable"}
                        scrollButtons="auto"
                        aria-label="full width tabs example"
                        sx={{
                            "& .css-1vw5i9y-MuiButtonBase-root-MuiTab-root": {
                                color: "#212121",
                                fontWeight: "bold",
                                textAlign: 'center'
                            },
                            "& .MuiTab-root": {
                                paddingBottom: 4
                            },
                            "& .Mui-selected": {
                                bgcolor: "#0277bd !important",
                                color: "#fafafa !important",
                                fontWeight: "bold !important",
                                paddingBottom: 4
                            }
                        }}
                        TabIndicatorProps={{ style: { background: "#0277bd" } }}
                    >

                        {/* Stock */}
                        <Tab
                            icon={<DiamondIcon />}
                            iconPosition="start"
                            label="Stock"
                            {...a11yProps(0)}
                        />

                        {/* Box */}
                        <Tab
                            icon={<FaBoxOpen size={24} />}
                            iconPosition="start"
                            label="Box"
                            {...a11yProps(1)}
                        />

                        {/* Grading */}
                        <Tab
                            icon={<GradingIcon />}
                            iconPosition="start"
                            label="Grading"
                            {...a11yProps(2)}
                        />

                        {/* Proforma Send */}
                        <Tab
                            icon={<SendIcon />}
                            iconPosition="start"
                            label="Proforma Send"
                            {...a11yProps(3)}
                        />

                        {/* Proforma Receive */}
                        <Tab
                            icon={<SendAndArchiveIcon />}
                            iconPosition="start"
                            label="Proforma Receive"
                            {...a11yProps(4)}
                        />

                        {/* Manufacturing Send */}
                        <Tab
                            icon={<LocalShippingIcon />}
                            iconPosition="start"
                            label="Manufacturing Send"
                            {...a11yProps(5)}
                        />

                        {/* Manufacturing Receive */}
                        <Tab
                            icon={<LocalShippingIcon size={20} sx={{ transform: "scaleX(-1)" }} />}
                            iconPosition="start"
                            label="Manufacturing Receive"
                            {...a11yProps(6)}
                        />

                        {/* Location Transfer Send */}
                        <Tab
                            icon={<NearMeIcon />}
                            iconPosition="start"
                            label="Location Transfer Send"
                            {...a11yProps(7)}
                        />

                        {/* Location Transfer Receive */}
                        <Tab
                            icon={<NearMeIcon size={23} sx={{ rotate: "180deg" }} />}
                            iconPosition="start"
                            label="Location Transfer Receive"
                            {...a11yProps(8)}
                        />

                        {/* Offline */}
                        <Tab
                            icon={<UnpublishedIcon size={23} />}
                            iconPosition="start"
                            label="Offline"
                            {...a11yProps(9)}
                        />
                    </Tabs>
                </AppBar>
                <SwipeableViews
                    axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                    index={value}
                    onChangeIndex={handleChangeIndex}
                >
                    {/* Stock */}
                    <TabPanel value={value} index={0}>
                        <TallyStock />
                    </TabPanel>

                    {/* Box */}
                    <TabPanel value={value} index={1}>
                        <TallyBox />
                    </TabPanel>

                    {/* Grading */}
                    <TabPanel value={value} index={2}>
                        <TallyGrading />
                    </TabPanel>

                    {/* Proforma Send */}
                    <TabPanel value={value} index={3}>
                        <TallyProformaSend />
                    </TabPanel>

                    {/* Proforma Receive */}
                    <TabPanel value={value} index={4}>
                        <TallyProformaReceive />
                    </TabPanel>

                    {/* Manufacturing Send */}
                    <TabPanel value={value} index={5}>
                        <TallyManufacturingSend />
                    </TabPanel>

                    {/* Manufacturing Receive */}
                    <TabPanel value={value} index={6}>
                        <TallyManufacturingReceive />
                    </TabPanel>

                    {/* Location Transfer Send */}
                    <TabPanel value={value} index={7}>
                        <TallyLocationTransferSend />
                    </TabPanel>

                    {/* Location Transfer Receive */}
                    <TabPanel value={value} index={8}>
                        <TallyLocationTransferReceive />
                    </TabPanel>

                    {/* Offline */}
                    <TabPanel value={value} index={9}>
                        <TallyOffline />
                    </TabPanel>
                </SwipeableViews>
            </Box>
    );
}
